import { API_ENCRYPT } from "./crypto";
import { getToken } from "./token";

/**
 * Headers every request from this UI carries.
 *
 * Functions rather than constants, because one of these headers is now the
 * bearer token: a module-level object would be built once at import, before any
 * sign-in has happened, and would then carry a stale token (or none) for the
 * lifetime of the page. Read at call time, it is always the current one.
 *
 * `x-api-encrypt` follows the build's own setting rather than being hardcoded,
 * so the header and the client's ability to decrypt can never disagree. Telling
 * the server to encrypt while this bundle was built without a key would turn
 * every response into an unreadable string; telling it not to while this bundle
 * expects ciphertext is merely wasteful, but equally a lie.
 *
 * Off by default. See api/crypto.ts for why enabling it is a testing
 * affordance and not a security control: with it on, the key is in the bundle.
 */
export function apiHeaders(): Record<string, string> {
  const token = getToken();
  return {
    Accept: "application/json",
    "x-api-encrypt": API_ENCRYPT ? "true" : "false",
    // Omitted entirely when there is none, rather than sent empty: the server
    // reads any Authorization header as an attempt to present a credential and
    // answers BAD_TOKEN, which would turn "not signed in yet" into a confusing
    // "your token is wrong" on the very first call.
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** The same, for requests that send a JSON body. */
export function apiHeadersJson(): Record<string, string> {
  return { ...apiHeaders(), "Content-Type": "application/json" };
}
